import { useEffect, useState } from 'react'
import { CheckCircle, XCircle, Clock, CreditCard, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../services/api'
import type { InvoicePayment, InvoicePaymentStatus } from '../types'

function StatusBadge({ status }: { status: InvoicePaymentStatus }) {
  const { t } = useTranslation()
  if (status === 'succeeded')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
        <CheckCircle size={11} />
        {t('payment.statusSucceeded', 'Payé')}
      </span>
    )
  if (status === 'failed')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
        <XCircle size={11} />
        {t('payment.statusFailed', 'Échoué')}
      </span>
    )
  if (status === 'cancelled')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
        {t('payment.statusCancelled', 'Annulé')}
      </span>
    )
  // pending / refunded
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
      <Clock size={11} />
      {status === 'refunded'
        ? t('payment.statusRefunded', 'Remboursé')
        : t('payment.statusPending', 'En attente')}
    </span>
  )
}

export default function MyPayments() {
  const { t } = useTranslation()
  const [payments, setPayments] = useState<InvoicePayment[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api
      .getMyPayments()
      .then(setPayments)
      .catch(() => setPayments([]))
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t('payment.myPaymentsTitle', 'Mes paiements')}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
          {t('payment.myPaymentsSubtitle', 'Historique de vos paiements de factures')}
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16 text-gray-400">
          <RefreshCw size={24} className="animate-spin" />
        </div>
      ) : payments.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <CreditCard size={40} className="mx-auto mb-3 opacity-40" />
          <p>{t('payment.noPayments', 'Aucun paiement enregistré.')}</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Mobile card layout */}
          <ul className="divide-y divide-gray-100 dark:divide-gray-700 sm:hidden">
            {payments.map((p) => (
              <li key={p.id} className="p-4 space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-gray-900 dark:text-white text-sm">
                    {p.invoice_ref ?? p.invoice_id}
                  </p>
                  <StatusBadge status={p.status} />
                </div>
                <div className="flex items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span>
                    {p.payment_type === 'subscription'
                      ? t('payment.typeSubscription', 'Récurrent')
                      : t('payment.typeOneTime', 'Ponctuel')}
                  </span>
                  <span>
                    {p.paid_at
                      ? new Date(p.paid_at).toLocaleDateString('fr-FR')
                      : new Date(p.created_at).toLocaleDateString('fr-FR')}
                  </span>
                </div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {p.amount.toLocaleString('fr-FR', {
                    style: 'currency',
                    currency: p.currency.toUpperCase(),
                  })}
                </p>
              </li>
            ))}
          </ul>
          {/* Desktop table layout */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/40">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {t('payment.invoiceRef', 'Facture')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {t('payment.amount', 'Montant')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {t('payment.type', 'Type')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {t('payment.status', 'Statut')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {t('payment.date', 'Date')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {payments.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                      {p.invoice_ref ?? p.invoice_id}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-200">
                      {p.amount.toLocaleString('fr-FR', {
                        style: 'currency',
                        currency: p.currency.toUpperCase(),
                      })}
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                      {p.payment_type === 'subscription'
                        ? t('payment.typeSubscription', 'Récurrent')
                        : t('payment.typeOneTime', 'Ponctuel')}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={p.status} />
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                      {p.paid_at
                        ? new Date(p.paid_at).toLocaleDateString('fr-FR')
                        : new Date(p.created_at).toLocaleDateString('fr-FR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
